import { useState } from 'react'
import { CheckCircle, XCircle, MinusCircle } from 'lucide-react'
import type { TracerStep as TracerStepType } from '../../lib/services/survey'
import { VoiceRecorder } from './VoiceRecorder'
import { PhotoCapture } from './PhotoCapture'
import type { FacilityArea } from '../../lib/services/survey'

interface TracerStepProps {
  step: TracerStepType
  visitId: string
  surveyId: string
  area: FacilityArea
  onChange: (updated: TracerStepType) => void
}

const RESULT_OPTIONS: { value: 'pass' | 'fail' | 'na'; icon: typeof CheckCircle; color: string }[] = [
  { value: 'pass', icon: CheckCircle, color: 'text-emerald-600' },
  { value: 'fail', icon: XCircle, color: 'text-red-600' },
  { value: 'na', icon: MinusCircle, color: 'text-slate-400' },
]

export function TracerStepEntry({ step, visitId, surveyId, area, onChange }: TracerStepProps) {
  const [observation, setObservation] = useState(step.observation)
  const [interviewNotes, setInterviewNotes] = useState(step.interview_notes ?? '')
  const [standardRef, setStandardRef] = useState(step.standard_ref)

  const updateField = (patch: Partial<TracerStepType>) => {
    onChange({ ...step, ...patch })
  }

  return (
    <div className="app-surface p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
          Step {step.order}
        </span>
        <div className="flex items-center gap-1">
          {RESULT_OPTIONS.map(({ value, icon: Icon, color }) => (
            <button
              key={value}
              type="button"
              onClick={() => updateField({ result: value })}
              className={`p-1.5 rounded-lg transition-colors ${
                step.result === value ? `${color} bg-slate-100` : 'text-slate-300 hover:text-slate-500'
              }`}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
      </div>

      <input
        type="text"
        placeholder="Standard reference (e.g. IC.02.02.01)"
        value={standardRef}
        onChange={(e) => { setStandardRef(e.target.value); updateField({ standard_ref: e.target.value }) }}
        className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
      />

      <textarea
        placeholder="Observation…"
        value={observation}
        onChange={(e) => { setObservation(e.target.value); updateField({ observation: e.target.value }) }}
        rows={2}
        className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
      />

      <textarea
        placeholder="Staff interview notes (optional)…"
        value={interviewNotes}
        onChange={(e) => { setInterviewNotes(e.target.value); updateField({ interview_notes: e.target.value || null }) }}
        rows={2}
        className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
      />

      <div className="flex items-center gap-2">
        <PhotoCapture
          visitId={visitId}
          surveyId={surveyId}
          area={area}
          onCapture={(photo) => updateField({ photo_ids: [...step.photo_ids, photo.photo_id] })}
        />
        <VoiceRecorder
          onTranscript={(r) => {
            const updated = observation ? `${observation}\n${r.text}` : r.text
            setObservation(updated)
            updateField({ observation: updated })
          }}
        />
      </div>
    </div>
  )
}
